/**
 * 2D spatialisation (07 §2): pan by the x offset from the listener, gain and a low-pass by
 * distance. The view is only about 36 x 28 m but sounds carry 60 m, so a patrol is HEARD to the
 * north - quiet, dull, slightly to one side - well before it walks on screen.
 * Pure maths: the engine applies the result to a panner, a gain and a filter.
 */

export const SPATIAL = {
  /** Inside this radius a sound is at full level and full brightness. */
  refDistance: 6,
  /** Default range of a sound, metres; loud sounds set their own (`SfxDef.maxDistance`). */
  maxDistance: 60,
  /** How fast level falls with distance: 1 would be the physical inverse law; less carries further. */
  rolloff: 0.8,
  /** Metres east or west at which a sound sits fully to one side: about half the view's width. */
  panWidth: 20,
  /** Never hard-pan: a sound in one ear only is unpleasant on headphones. */
  maxPan: 0.85,
  /** Low-pass cutoff at the listener and at the edge of a sound's range, Hz. */
  nearCutoff: 18000,
  farCutoff: 900,
  /** Through a wall (07 §2): -6 dB and a 1.2 kHz low-pass. */
  occludedGain: 0.5,
  occludedCutoff: 1200,
} as const;

/** Arctic air and snow swallow sound (01 §10): every world sound is low-passed and 3 dB down. */
export const ARCTIC_MUFFLE = { gain: Math.pow(10, -3 / 20), cutoff: 5200 } as const;
export const OPEN_AIR = { gain: 1, cutoff: 20000 } as const;

export interface Spatial {
  pan: number;
  gain: number;
  cutoff: number;
  distance: number;
}

export const createSpatial = (): Spatial => ({ pan: 0, gain: 1, cutoff: SPATIAL.nearCutoff, distance: 0 });

/**
 * Where a sound at offset (`dx` east, `dz` south) of the listener sits in the mix. Writes into
 * `out` and returns it, so the per-frame callers allocate nothing.
 */
export function spatialise(dx: number, dz: number, maxDistance: number, occluded: boolean, out: Spatial): Spatial {
  const d = Math.sqrt(dx * dx + dz * dz);
  const ref = SPATIAL.refDistance;
  const max = Math.max(ref + 1, maxDistance);
  out.distance = d;
  out.pan = Math.max(-1, Math.min(1, dx / SPATIAL.panWidth)) * SPATIAL.maxPan;
  if (d <= ref) {
    out.gain = 1;
    out.cutoff = SPATIAL.nearCutoff;
  } else if (d >= max) {
    out.gain = 0;
    out.cutoff = SPATIAL.farCutoff;
  } else {
    const t = (d - ref) / (max - ref);
    // Inverse-distance rolloff, windowed so it reaches exactly zero at the edge of its range.
    out.gain = Math.pow(ref / d, SPATIAL.rolloff) * (1 - t * t);
    out.cutoff = SPATIAL.nearCutoff * Math.pow(SPATIAL.farCutoff / SPATIAL.nearCutoff, t);
  }
  if (occluded) {
    out.gain *= SPATIAL.occludedGain;
    out.cutoff = Math.min(out.cutoff, SPATIAL.occludedCutoff);
  }
  return out;
}

/** Decibels to linear gain. */
export const dbToGain = (db: number): number => Math.pow(10, db / 20);
